import React, { useState, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import WelcomeScreen from "@/components/clarity/WelcomeScreen";
import StepEntry from "@/components/clarity/StepEntry";
import StepFrameValues from "@/components/clarity/StepFrameValues";
import StepAlternativesInfo from "@/components/clarity/StepAlternativesInfo";
import StepEvaluate from "@/components/clarity/StepEvaluate";
import StepTestCommit from "@/components/clarity/StepTestCommit";
import StepNextSteps from "@/components/clarity/StepNextSteps";
import ProgressSidebar from "@/components/clarity/ProgressSidebar";
import DecisionSummary from "@/components/clarity/DecisionSummary";
import Footer from "@/components/clarity/Footer";

const STEPS = [
  { id: 0, name: "Welcome", shortName: "Start" },
  { id: 1, name: "Entry", shortName: "Entry" },
  { id: 2, name: "Frame & Values", shortName: "Frame" },
  { id: 3, name: "Alternatives & Info", shortName: "Options" },
  { id: 4, name: "Evaluate", shortName: "Evaluate" },
  { id: 5, name: "Test & Commit", shortName: "Test" },
  { id: 6, name: "Next Steps", shortName: "Action" },
];

const initialDecisionData = {
  // Entry
  whatToAchieve: "",
  whyChallenging: "",
  
  // Frame
  decisionsIFace: [],
  whoElseInvolved: [],
  deadline: "",
  
  // Values
  whatIsAtStake: [],
  whatIWant: [],
  whatIWantToAvoid: [],
  tradeoffs: "",
  
  // Alternatives
  optionsISee: [],
  constraintFreeOptions: "",
  creativeApproaches: [],
  
  // Information
  whatINeedToKnow: [],
  whoToTalkTo: [],
  uncertainties: [],
  sourceTrustworthiness: "",
  
  // Evaluation ratings (0-100)
  ratings: {
    helpfulFrame: 0,
    clearValues: 0,
    creativeAlternatives: 0,
    usefulInformation: 0,
    soundReasoning: 0,
    commitmentToFollow: 0,
  },
  
  // Test & Commit
  amIFitToDecide: null,
  amIReadyToDecide: null,
  doesItMakeSense: null,
  doesItFeelRight: null,
  
  // Commitment
  commitment: "",
  nextSteps: [],
  targetDate: "",
};

const ConversationsForClarity = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [decisionData, setDecisionData] = useState(initialDecisionData);
  const [showSummary, setShowSummary] = useState(false);
  const [coachMode, setCoachMode] = useState(true); // Coach mode ON by default

  const toggleCoachMode = useCallback(() => {
    setCoachMode(prev => !prev);
  }, []);

  const updateDecisionData = useCallback((field, value) => {
    setDecisionData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const updateRating = useCallback((ratingField, value) => {
    setDecisionData(prev => ({
      ...prev,
      ratings: {
        ...prev.ratings,
        [ratingField]: value
      }
    }));
  }, []);

  const goToStep = useCallback((step) => {
    if (step >= 0 && step <= 6) {
      setCurrentStep(step);
      setShowSummary(false);
    }
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep < 6) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const handleStartOver = useCallback(() => {
    setDecisionData(initialDecisionData);
    setCurrentStep(0);
    setShowSummary(false);
  }, []);

  const handleViewSummary = useCallback(() => {
    setShowSummary(true);
  }, []);

  const renderCurrentStep = () => {
    if (showSummary) {
      return (
        <DecisionSummary 
          decisionData={decisionData}
          onBack={() => setShowSummary(false)}
          onStartOver={handleStartOver}
        />
      );
    }

    switch (currentStep) {
      case 0:
        return <WelcomeScreen onStart={() => goToStep(1)} coachMode={coachMode} onToggleCoach={toggleCoachMode} />;
      case 1:
        return (
          <StepEntry
            data={decisionData}
            updateData={updateDecisionData}
            onNext={nextStep}
            onBack={prevStep}
            coachMode={coachMode}
          />
        );
      case 2:
        return (
          <StepFrameValues
            data={decisionData}
            updateData={updateDecisionData}
            onNext={nextStep}
            onBack={prevStep}
            coachMode={coachMode}
          />
        );
      case 3:
        return (
          <StepAlternativesInfo
            data={decisionData}
            updateData={updateDecisionData}
            onNext={nextStep}
            onBack={prevStep}
            coachMode={coachMode}
          />
        );
      case 4:
        return (
          <StepEvaluate
            data={decisionData}
            updateRating={updateRating}
            onNext={nextStep}
            onBack={prevStep}
            coachMode={coachMode}
          />
        );
      case 5:
        return (
          <StepTestCommit
            data={decisionData}
            updateData={updateDecisionData}
            onNext={nextStep}
            onBack={prevStep}
            onIterate={() => goToStep(4)}
            coachMode={coachMode}
          />
        );
      case 6:
        return (
          <StepNextSteps
            data={decisionData}
            updateData={updateDecisionData}
            onBack={prevStep}
            onViewSummary={handleViewSummary}
            onStartOver={handleStartOver}
            coachMode={coachMode}
          />
        );
      default:
        return <WelcomeScreen onStart={() => goToStep(1)} coachMode={coachMode} onToggleCoach={toggleCoachMode} />;
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Main Layout */}
      <div className="flex flex-1">
        {/* Sidebar - only show after welcome */}
        {currentStep > 0 && !showSummary && (
          <ProgressSidebar
            steps={STEPS}
            currentStep={currentStep}
            decisionData={decisionData}
            onStepClick={goToStep}
            coachMode={coachMode}
            onToggleCoach={toggleCoachMode}
          />
        )}

        {/* Main Content */}
        <main className={`flex-1 ${currentStep > 0 && !showSummary ? 'lg:ml-72' : ''}`}>
          <AnimatePresence mode="wait">
            {renderCurrentStep()}
          </AnimatePresence>
        </main>
      </div>
      
      {/* Footer - show on welcome and summary pages */}
      {(currentStep === 0 || showSummary) && <Footer />}
    </div>
  );
};

export default ConversationsForClarity;
